// frontend/src/super-admin/PlatformAuditPage.tsx
//
// Platform-wide audit feed (super-admin only). Read-only table of every audit
// row — both tenant-scoped actions (member/role changes) and platform-level
// actions (tenant suspend/reactivate/trial-extend/module change, subscription
// cancel, module-price changes). Gated by `tenants.read` (route + nav).
// Backed by GET /api/admin/tenants/audit → listPlatformAudit.

import * as React from 'react';
import { ScrollText, Search } from 'lucide-react';
import { api } from '../core/api-client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { LoadingState } from '../design-system/components/LoadingState';
import { ErrorState } from '../design-system/components/ErrorState';
import { EmptyState } from '../design-system/components/EmptyState';
import { PageHeader } from '../design-system/components/PageHeader';
import { VISUAL, statusPill } from '../design-system/visual';
import { formatDateTime } from '../design-system/format';

const PAGE = 50;

interface PlatformAuditEntry {
  id: string;
  tenant_id: string | null;
  actor_label: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  summary: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export function PlatformAuditPage() {
  const [entries, setEntries] = React.useState<PlatformAuditEntry[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [offset, setOffset] = React.useState(0);

  // Search is server-side: the audit endpoint accepts `q` (substring across
  // action/summary/actor_label), so it filters the whole trail. Scope maps to
  // tenant_id-presence, which is not a server param — it stays client-side
  // over the loaded page.
  const [scope, setScope] = React.useState<'all' | 'tenant' | 'platform'>('all');
  const [query, setQuery] = React.useState('');
  const [appliedQ, setAppliedQ] = React.useState('');

  // Debounce the search box into the applied server param; reset paging with
  // it so a new search always starts at the first page.
  React.useEffect(() => {
    const q = query.trim();
    if (q === appliedQ) return;
    const t = setTimeout(() => { setAppliedQ(q); setOffset(0); }, 350);
    return () => clearTimeout(t);
  }, [query, appliedQ]);

  // Guard against out-of-order responses (fast typing across the debounce).
  const reqRef = React.useRef(0);
  const load = React.useCallback((off: number, q: string) => {
    const id = ++reqRef.current;
    setError(null);
    const params = new URLSearchParams({ limit: String(PAGE), offset: String(off) });
    if (q) params.set('q', q);
    api<{ entries: PlatformAuditEntry[] }>(`/api/admin/tenants/audit?${params.toString()}`)
      .then((r) => { if (reqRef.current === id) setEntries(r.entries); })
      .catch((e) => { if (reqRef.current === id) setError(String(e?.message ?? e)); });
  }, []);

  React.useEffect(() => { load(offset, appliedQ); }, [load, offset, appliedQ]);

  const visible = React.useMemo(() => {
    if (!entries) return [];
    return entries.filter((e) => (
      scope === 'all' ? true :
      scope === 'tenant' ? e.tenant_id != null :
      e.tenant_id == null
    ));
  }, [entries, scope]);

  const filtersActive = scope !== 'all' || appliedQ !== '';
  // No total from the API — a full page implies there may be more.
  const hasMore = (entries?.length ?? 0) === PAGE;

  if (error) return <ErrorState description={error} onRetry={() => load(offset, appliedQ)} />;
  if (!entries) return <LoadingState variant="page" />;

  return (
    <div>
      <PageHeader
        eyebrow="Operations / Audit"
        title="Platform audit"
        description="A read-only trail of sensitive actions across all tenants and the platform."
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-14 space-y-4">
        {(entries.length > 0 || filtersActive) && (
          <section className="flex items-center gap-3">
            <Select value={scope} onValueChange={(v) => setScope(v as 'all' | 'tenant' | 'platform')}>
              <SelectTrigger className="h-10 w-auto min-w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All scopes</SelectItem>
                <SelectItem value="tenant">Tenant</SelectItem>
                <SelectItem value="platform">Platform</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1 max-w-md">
              <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                type="search"
                placeholder="Search by action, summary or actor…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-10 pl-9 rounded-full bg-card/80 border-border/60 text-[13px]"
              />
            </div>
            {filtersActive && (
              <button
                type="button"
                onClick={() => { setScope('all'); setQuery(''); setAppliedQ(''); setOffset(0); }}
                className="text-[12px] font-semibold text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            )}
          </section>
        )}

        {entries.length === 0 ? (
          filtersActive ? (
            <div className={[VISUAL.bento, 'py-14 px-6 text-center'].join(' ')}>
              <p className="text-[14px] font-semibold text-foreground">No entries match your search</p>
              <p className="text-[12.5px] text-muted-foreground mt-1">Search covers the whole trail. Try a different query or clear the filters.</p>
            </div>
          ) : (
          <div className={[VISUAL.bento, 'p-2'].join(' ')}>
            <EmptyState
              icon={<ScrollText />}
              tone="muted"
              title="No audit activity yet"
              description="Tenant suspensions, module changes, subscription and pricing actions, and workspace role changes will appear here."
            />
          </div>
          )
        ) : (
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            {visible.length === 0 ? (
              <div className="py-14 px-6 text-center">
                <p className="text-[14px] font-semibold text-foreground">No entries match your filters</p>
                <p className="text-[12.5px] text-muted-foreground mt-1">Search covers the whole trail; the scope filter applies to this page only. Try clearing it or paging on.</p>
              </div>
            ) : (
            <ul className="divide-y divide-border/40">
              {visible.map((entry) => (
                <li key={entry.id} className="flex items-start gap-4 px-5 sm:px-6 py-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={statusPill(entry.tenant_id ? 'info' : 'warning')}>{entry.action}</span>
                      <span className={statusPill(entry.tenant_id ? 'sent' : 'draft')}>
                        {entry.tenant_id ? 'tenant' : 'platform'}
                      </span>
                      <p className="text-[14px] font-medium text-foreground truncate">{entry.summary}</p>
                    </div>
                    <p className="text-[12px] text-muted-foreground mt-1">
                      {entry.actor_label ?? 'Unknown'}
                      {entry.target_type ? ` · ${entry.target_type}` : ''}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-[12.5px] text-foreground/80 tabular-nums">{formatDateTime(entry.created_at)}</p>
                  </div>
                </li>
              ))}
            </ul>
            )}
          </div>
        )}

        {(offset > 0 || hasMore) && (
          <div className="flex items-center justify-between text-[12.5px] text-muted-foreground">
            <span>Showing {offset + 1}–{offset + entries.length}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full h-9" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
              <Button size="sm" variant="outline" className="rounded-full h-9" disabled={!hasMore} onClick={() => setOffset(offset + PAGE)}>Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
